import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Eye, QrCode, Download, ExternalLink, ArrowLeft, ChevronUp, ChevronDown, Search, FileSpreadsheet, PenSquare, PowerOff, Power } from "lucide-react";
import { DashboardShell, StatCard, SectionTitle, Btn, Input, Modal, EmptyState } from "@/components/DashUI";

const QUESTION_TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice", label: "Multiple choice" },
  { value: "contact_dynamic", label: "Contact details (dynamic)" },
  { value: "contact_conditional", label: "Contact details (conditional)" },
];

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  converted: "Converted",
  closed: "Closed",
};

export function LeadForms({ nav }) {
  const location = useLocation();
  const basePath = location.pathname.startsWith("/admin") ? "/admin/lead-forms" : "/dashboard/lead-forms";
  return (
    <Routes>
      <Route index element={<LeadFormsList nav={nav} basePath={basePath} />} />
      <Route path=":formId" element={<LeadFormEdit nav={nav} basePath={basePath} />} />
      <Route path=":formId/leads" element={<LeadFormLeads nav={nav} basePath={basePath} />} />
      <Route path=":formId/analytics" element={<LeadFormAnalytics nav={nav} basePath={basePath} />} />
    </Routes>
  );
}

/* ==================== List ==================== */
function LeadFormsList({ nav, basePath }) {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [me, setMe] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", event_id: "", welcome_message: "", org_id: "" });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const isSuper = me?.role === "super_admin";

  async function refresh() {
    const { data } = await api.get("/lead-forms");
    setRows(data);
  }
  useEffect(() => {
    refresh();
    api.get("/auth/me").then((r) => setMe(r.data));
    api.get("/events").then((r) => setEvents(r.data));
    // Only super admin needs org list
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);

  // Filter events by selected org for super admin
  const availableEvents = isSuper && form.org_id ? events.filter((e) => e.org_id === form.org_id) : (isSuper ? [] : events);
  // Enrich rows with org names for super admin
  const orgById = Object.fromEntries(orgs.map((o) => [o.id, o.name]));

  async function create(e) {
    e.preventDefault();
    if (isSuper && !form.org_id) { toast.error("Please pick an organization"); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.event_id) delete payload.event_id;
      if (!isSuper) delete payload.org_id;
      const { data } = await api.post("/lead-forms", payload);
      toast.success("Lead form created");
      setModal(false);
      setForm({ name: "", description: "", event_id: "", welcome_message: "", org_id: "" });
      navigate(`${basePath}/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  }

  async function toggleActive(f) {
    try { await api.patch(`/lead-forms/${f.id}`, { active: !f.active }); refresh(); }
    catch { toast.error("Failed to toggle"); }
  }
  async function duplicate(f) {
    try { await api.post(`/lead-forms/${f.id}/duplicate`); toast.success("Duplicated"); refresh(); }
    catch { toast.error("Duplicate failed"); }
  }
  async function del(f) {
    if (!window.confirm(`Delete "${f.name}" and all its leads?`)) return;
    try { await api.delete(`/lead-forms/${f.id}`); refresh(); }
    catch { toast.error("Delete failed"); }
  }

  return (
    <DashboardShell title="Lead Forms" subtitle="Lead Forms" nav={nav}>
      <SectionTitle title="All lead forms" action={<Btn onClick={() => setModal(true)} testId="new-lead-form-btn"><Plus size={14} /> New form</Btn>} />
      {rows.length === 0 ? (
        <EmptyState icon={<PenSquare size={18} />} title="No lead forms yet" body="Create a dedicated lead-generation form with its own QR code, separate from your AI assistant." action={<Btn onClick={() => setModal(true)}><Plus size={14} /> New form</Btn>} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((f) => (
            <div key={f.id} className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors" data-testid={`lead-form-card-${f.slug}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${f.active ? "bg-green-500" : "bg-gray-300"}`} />
                    {f.active ? "Active" : "Inactive"}
                  </div>
                  <div className="font-display text-lg font-semibold text-gray-900 mt-1 truncate">{f.name}</div>
                  {isSuper && orgById[f.org_id] && (
                    <div className="text-xs text-purple-700 mt-0.5 truncate" data-testid={`form-org-${f.slug}`}>{orgById[f.org_id]}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1 font-mono">/lead/{f.slug}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="bg-gray-100 rounded-md px-2 py-1 text-gray-700">{f.questions?.length || 0} Q</span>
                <span className="bg-purple-50 rounded-md px-2 py-1 text-purple-700">{f.leads_count || 0} leads</span>
                <span className="bg-gray-100 rounded-md px-2 py-1 text-gray-700">{f.views || 0} views</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="secondary" onClick={() => navigate(`${basePath}/${f.id}`)} testId={`edit-${f.slug}`}><PenSquare size={14} /> Edit</Btn>
                <Btn variant="secondary" onClick={() => navigate(`${basePath}/${f.id}/leads`)} testId={`leads-${f.slug}`}>Leads</Btn>
                <Btn variant="secondary" onClick={() => navigate(`${basePath}/${f.id}/analytics`)} testId={`analytics-${f.slug}`}>Analytics</Btn>
              </div>
              <div className="mt-2 flex gap-3 text-xs text-gray-500">
                <button onClick={() => toggleActive(f)} className="hover:text-gray-900 inline-flex items-center gap-1" data-testid={`toggle-${f.slug}`}>
                  {f.active ? <PowerOff size={12} /> : <Power size={12} />} {f.active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => duplicate(f)} className="hover:text-gray-900 inline-flex items-center gap-1" data-testid={`duplicate-${f.slug}`}><Copy size={12} /> Duplicate</button>
                <button onClick={() => del(f)} className="hover:text-red-600 inline-flex items-center gap-1" data-testid={`delete-${f.slug}`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Create lead form">
        <form onSubmit={create} className="space-y-3" data-testid="new-lead-form">
          {isSuper && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Organization <span className="text-pink-600">*</span></label>
              <select value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value, event_id: "" })} required className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="form-org">
                <option value="">Select an organization…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <Input label="Form name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Mining Expo 2026 Lead Form" testId="form-name" />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Link to event (optional)</label>
            <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="form-event" disabled={isSuper && !form.org_id}>
              <option value="">— None —</option>
              {availableEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
            {isSuper && !form.org_id && <div className="text-[10px] text-gray-400 mt-1">Pick an organization first.</div>}
          </div>
          <Input label="Welcome message" rows={3} value={form.welcome_message} onChange={(v) => setForm({ ...form, welcome_message: v })} placeholder="Tell us what you're interested in and our team will follow up." testId="form-welcome" />
          <p className="text-xs text-gray-500">The default 6 questions will be created automatically. You can edit them next.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn type="submit" disabled={creating} testId="submit-new-form">{creating ? "Creating…" : "Create"}</Btn>
          </div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

/* ==================== Edit ==================== */
function LeadFormEdit({ nav, basePath }) {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [kioskUrl, setKioskUrl] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [kioskQrSrc, setKioskQrSrc] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  async function refresh() {
    const { data } = await api.get(`/lead-forms/${formId}`);
    setForm(data);
    const { data: u } = await api.get(`/lead-forms/${formId}/public-url`);
    setPublicUrl(u.url);
    setKioskUrl(u.kiosk_url || `${u.url}?kiosk=1`);
    const qr = await api.get(`/lead-forms/${formId}/qr.png`, { responseType: "blob" });
    setQrSrc(URL.createObjectURL(qr.data));
    const qrK = await api.get(`/lead-forms/${formId}/qr.png?kiosk=1`, { responseType: "blob" });
    setKioskQrSrc(URL.createObjectURL(qrK.data));
  }
  useEffect(() => { refresh(); return () => { if (qrSrc) URL.revokeObjectURL(qrSrc); if (kioskQrSrc) URL.revokeObjectURL(kioskQrSrc); }; /* eslint-disable-next-line */ }, [formId]);

  if (!form) return <DashboardShell title="Lead Forms" subtitle="Edit" nav={nav}><div>Loading…</div></DashboardShell>;

  function updateQuestion(idx, patch) {
    setForm((f) => {
      const qs = [...f.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...f, questions: qs };
    });
  }
  function moveQuestion(idx, dir) {
    setForm((f) => {
      const qs = [...f.questions];
      const to = idx + dir;
      if (to < 0 || to >= qs.length) return f;
      const [item] = qs.splice(idx, 1);
      qs.splice(to, 0, item);
      qs.forEach((q, i) => { q.order = i + 1; });
      return { ...f, questions: qs };
    });
  }
  function removeQuestion(idx) {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i + 1 })) }));
  }
  function addQuestion() {
    setForm((f) => {
      if (f.questions.length >= 10) { toast.error("Maximum 10 questions"); return f; }
      const newQ = { id: `q-${Math.random().toString(36).slice(2, 10)}`, label: "New question", type: "short_text", required: false, options: [], order: f.questions.length + 1 };
      return { ...f, questions: [...f.questions, newQ] };
    });
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lead-forms/${formId}`, {
        name: form.name, description: form.description, welcome_message: form.welcome_message,
        thank_you_message: form.thank_you_message, redirect_url: form.redirect_url,
        active: form.active, consent_text: form.consent_text, questions: form.questions,
        event_id: form.event_id, collect_full_name: form.collect_full_name,
        allow_anonymous_name: !!form.allow_anonymous_name,
        show_privacy_notice: !!form.show_privacy_notice, privacy_notice_text: form.privacy_notice_text || "",
      });
      setForm(data);
      toast.success("Saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function download(kind, kiosk = false) {
    const res = await api.get(`/lead-forms/${formId}/qr.${kind}${kiosk ? "?kiosk=1" : ""}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${form.slug}${kiosk ? "-kiosk" : ""}-qr.${kind}`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <DashboardShell title="Lead Forms" subtitle={form.name} nav={nav}>
      <button onClick={() => navigate(`${basePath}`)} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1.5 mb-4" data-testid="back-to-forms">
        <ArrowLeft size={14} /> All forms
      </button>

      <div className="grid lg:grid-cols-3 gap-8 max-w-6xl">
        {/* Left: settings + questions */}
        <div className="lg:col-span-2 space-y-6">
          <SectionTitle title="Form details" />
          <Input label="Form name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="edit-name" />
          <Input label="Welcome message" rows={3} value={form.welcome_message} onChange={(v) => setForm({ ...form, welcome_message: v })} testId="edit-welcome" />
          <Input label="Thank-you message" rows={2} value={form.thank_you_message} onChange={(v) => setForm({ ...form, thank_you_message: v })} testId="edit-thankyou" />
          <Input label="Redirect URL after submit (optional)" value={form.redirect_url} onChange={(v) => setForm({ ...form, redirect_url: v })} placeholder="https://" testId="edit-redirect" />
          <Input label="Consent statement" rows={3} value={form.consent_text} onChange={(v) => setForm({ ...form, consent_text: v })} testId="edit-consent" />

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">Full Name</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.collect_full_name !== false}
                onChange={(e) => setForm({ ...form, collect_full_name: e.target.checked })}
                className="w-4 h-4 accent-purple-600"
                data-testid="collect-full-name-toggle"
              />
              <span className="text-sm text-gray-900">Collect visitor name</span>
            </label>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              When disabled, the Full Name question is hidden from the public form. Visitors can submit their contact details without providing a name.
            </p>
            {form.collect_full_name !== false && (
              <label className="flex items-center gap-3 cursor-pointer mt-4 pt-4 border-t border-gray-100">
                <input type="checkbox" checked={!!form.allow_anonymous_name}
                  onChange={(e) => setForm({ ...form, allow_anonymous_name: e.target.checked })}
                  className="w-4 h-4 accent-purple-600" data-testid="allow-anonymous-toggle" />
                <span className="text-sm text-gray-900">Allow anonymous</span>
                <span className="text-xs text-gray-500">Visitors can tap "Stay anonymous" and the lead is saved as "Anonymous".</span>
              </label>
            )}
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">Privacy Notice (kiosk welcome screen)</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.show_privacy_notice}
                onChange={(e) => setForm({ ...form, show_privacy_notice: e.target.checked })}
                className="w-4 h-4 accent-purple-600" data-testid="show-privacy-toggle" />
              <span className="text-sm text-gray-900">Show privacy notice before visitors start</span>
            </label>
            {form.show_privacy_notice && (
              <div className="mt-3">
                <Input label="Notice text" rows={5} value={form.privacy_notice_text} onChange={(v) => setForm({ ...form, privacy_notice_text: v })}
                  placeholder="Explain how personal information is collected and processed…" testId="edit-privacy-text" />
              </div>
            )}
          </div>

          <SectionTitle title={`Questions (${form.questions?.length || 0} / 10)`} action={
            <Btn variant="secondary" onClick={addQuestion} disabled={form.questions.length >= 10} testId="add-question">
              <Plus size={14} /> Add question
            </Btn>
          } />
          {form.questions.length >= 10 && (
            <div className="text-xs text-purple-700 -mt-3 mb-2" data-testid="max-questions-hint">Maximum 10 questions reached.</div>
          )}

          <div className="space-y-3" data-testid="questions-list">
            {form.questions.map((q, idx) => (
              <div key={q.id} className="border border-gray-200 rounded-xl p-4" data-testid={`question-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">Q{idx + 1}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveQuestion(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-900 disabled:opacity-30 p-1" data-testid={`up-${idx}`}><ChevronUp size={16} /></button>
                    <button onClick={() => moveQuestion(idx, 1)} disabled={idx === form.questions.length - 1} className="text-gray-400 hover:text-gray-900 disabled:opacity-30 p-1" data-testid={`down-${idx}`}><ChevronDown size={16} /></button>
                    <button onClick={() => removeQuestion(idx)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`remove-${idx}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <Input label="Question text" value={q.label} onChange={(v) => updateQuestion(idx, { label: v })} testId={`q-label-${idx}`} />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Type</label>
                    <select value={q.type} onChange={(e) => updateQuestion(idx, { type: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid={`q-type-${idx}`}>
                      {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <label className="flex items-end gap-2 text-sm">
                    <input type="checkbox" checked={!!q.required} onChange={(e) => updateQuestion(idx, { required: e.target.checked })} data-testid={`q-required-${idx}`} />
                    Required
                  </label>
                </div>
                {q.type === "contact_conditional" && <ConditionalFieldsPicker q={q} onChange={(fields) => updateQuestion(idx, { fields })} idx={idx} />}
                {(q.type === "single_choice" || q.type === "multi_choice") && (
                  <div className="mt-3">
                    <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Options (one per line)</label>
                    <textarea
                      rows={5}
                      value={(q.options || []).join("\n")}
                      onChange={(e) => updateQuestion(idx, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                      className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      data-testid={`q-options-${idx}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <Btn onClick={save} disabled={saving} testId="save-form">{saving ? "Saving…" : "Save changes"}</Btn>
        </div>

        {/* Right: QR + Public URL */}
        <div>
          <SectionTitle title="Public URL & QR" />
          <div className="border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-700 font-mono break-all" data-testid="form-public-url">{publicUrl}</div>
          <div className="flex gap-2 mt-3">
            <Btn variant="secondary" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} testId="copy-form-url"><Copy size={14} /> Copy</Btn>
            {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer"><Btn variant="secondary" testId="open-form"><ExternalLink size={14} /> Preview</Btn></a>}
          </div>
          <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center mt-4">
            {qrSrc ? <img src={qrSrc} alt="QR" className="w-48 h-48" data-testid="form-qr-img" /> : <div className="w-48 h-48 bg-gray-50 rounded-md animate-pulse" />}
            <div className="flex gap-2 mt-4">
              <Btn variant="secondary" onClick={() => download("png")} testId="dl-form-png"><Download size={14} /> PNG</Btn>
              <Btn variant="secondary" onClick={() => download("svg")} testId="dl-form-svg"><Download size={14} /> SVG</Btn>
            </div>
          </div>

          <SectionTitle title="Kiosk mode" />
          <p className="text-xs text-gray-500 -mt-2 mb-3">Full-screen exhibition kiosk — welcome screen, one question per screen, progress bar, and auto-reset 30 seconds after submit.</p>
          <div className="border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-700 font-mono break-all" data-testid="form-kiosk-url">{kioskUrl}</div>
          <div className="flex gap-2 mt-3">
            <Btn variant="secondary" onClick={() => { navigator.clipboard.writeText(kioskUrl); toast.success("Copied"); }} testId="copy-kiosk-url"><Copy size={14} /> Copy</Btn>
            {kioskUrl && <a href={kioskUrl} target="_blank" rel="noreferrer"><Btn variant="secondary" testId="open-kiosk"><ExternalLink size={14} /> Launch kiosk</Btn></a>}
          </div>
          <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center mt-4">
            {kioskQrSrc ? <img src={kioskQrSrc} alt="Kiosk QR" className="w-48 h-48" data-testid="kiosk-qr-img" /> : <div className="w-48 h-48 bg-gray-50 rounded-md animate-pulse" />}
            <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mt-3">Kiosk QR</div>
            <div className="flex gap-2 mt-3">
              <Btn variant="secondary" onClick={() => download("png", true)} testId="dl-kiosk-png"><Download size={14} /> PNG</Btn>
              <Btn variant="secondary" onClick={() => download("svg", true)} testId="dl-kiosk-svg"><Download size={14} /> SVG</Btn>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

/* ==================== Leads ==================== */
function LeadFormLeads({ nav, basePath }) {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  async function refresh() {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const { data } = await api.get(`/lead-forms/${formId}/leads?${params}`);
    setRows(data);
  }
  useEffect(() => {
    api.get(`/lead-forms/${formId}`).then((r) => setForm(r.data));
    refresh();
    /* eslint-disable-next-line */
  }, [formId, status]);

  async function exportCsv(kind) {
    const res = await api.get(`/lead-forms/${formId}/leads/export.${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${form?.slug || "leads"}.${kind}`; a.click();
    URL.revokeObjectURL(url);
  }

  async function updateLead(id, patch) {
    const { data } = await api.patch(`/form-leads/${id}`, patch);
    setRows((rs) => rs.map((r) => r.id === id ? data : r));
    setActive((a) => (a && a.id === id ? data : a));
  }

  return (
    <DashboardShell title="Lead Forms" subtitle={form?.name || "Leads"} nav={nav}>
      <button onClick={() => navigate(`${basePath}`)} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1.5 mb-4"><ArrowLeft size={14} /> All forms</button>
      <SectionTitle title={`Leads (${rows.length})`} action={
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={() => exportCsv("csv")} testId="export-form-csv"><Download size={14} /> CSV</Btn>
          <Btn variant="secondary" onClick={() => exportCsv("xlsx")} testId="export-form-xlsx"><FileSpreadsheet size={14} /> Excel</Btn>
        </div>
      } />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()} placeholder="Search…" className="border border-gray-200 rounded-md pl-8 pr-3 py-2 text-sm" data-testid="search-leads" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {rows.length === 0 ? <EmptyState title="No leads yet" body="Once visitors scan the QR and submit the form, leads appear here." /> :
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Received</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{l.full_name || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{l.contact_method ? `${l.contact_method}: ${l.contact_value || ""}` : "—"}</td>
                  <td className="px-4 py-3">
                    <select value={l.status} onChange={(e) => updateLead(l.id, { status: e.target.value })} className="text-xs border border-gray-200 rounded-md px-2 py-1" data-testid={`status-${l.id}`}>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setActive(l)} className="text-purple-700 hover:text-purple-900 text-xs" data-testid={`view-lead-${l.id}`}>View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      <Modal open={!!active} onClose={() => setActive(null)} title="Lead detail">
        {active && form && (
          <div className="space-y-4">
            <div className="text-xs text-gray-500">Received {(active.created_at || "").replace("T", " ").slice(0, 19)}</div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Status</label>
              <select value={active.status} onChange={(e) => updateLead(active.id, { status: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            {form.questions.map((q) => {
              const val = active.answers?.[q.id];
              const display = Array.isArray(val) ? val.join(", ")
                : (val && typeof val === "object") ? formatConditional(val) : (val || "—");
              return (
                <div key={q.id}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{q.label}</div>
                  <div className="text-sm text-gray-800 mt-1">{display}</div>
                </div>
              );
            })}

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Notes</label>
              <textarea rows={3} defaultValue={active.notes || ""} onBlur={(e) => updateLead(active.id, { notes: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="lead-notes" />
            </div>

            <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
              Consent: {active.consent_given ? "Yes" : "No"} · {active.consent_ts?.slice(0, 19).replace("T", " ")}
            </div>
          </div>
        )}
      </Modal>
    </DashboardShell>
  );
}

/* ==================== Analytics ==================== */
function LeadFormAnalytics({ nav, basePath }) {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/lead-forms/${formId}`).then((r) => setForm(r.data));
    api.get(`/lead-forms/${formId}/analytics`).then((r) => setStats(r.data));
  }, [formId]);

  return (
    <DashboardShell title="Lead Forms" subtitle={form?.name || "Analytics"} nav={nav}>
      <button onClick={() => navigate(`${basePath}`)} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1.5 mb-4"><ArrowLeft size={14} /> All forms</button>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Form views" value={stats?.views} testId="stat-views" />
        <StatCard label="Form starts" value={stats?.starts} testId="stat-starts" />
        <StatCard label="Completions" value={stats?.completions} testId="stat-completions" />
        <StatCard label="Completion rate" value={stats?.completion_rate != null ? `${stats.completion_rate}%` : "—"} testId="stat-rate" />
      </div>

      {/* Preferred contact — still a helpful roll-up */}
      {stats?.preferred_contact?.length > 0 && (
        <div className="mt-8">
          <BarList title="Preferred contact method" rows={stats.preferred_contact} labelKey="method" testId="bar-contact" />
        </div>
      )}

      {/* Per-question breakdown — one card per question in form order */}
      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-3">Question analytics</div>
        {(!stats?.questions_breakdown || stats.questions_breakdown.length === 0) ? (
          <div className="text-sm text-gray-500 border border-gray-200 rounded-xl p-6">No questions to analyse yet.</div>
        ) : (
          <div className="space-y-4" data-testid="lf-questions-breakdown">
            {stats.questions_breakdown.map((q) => <QuestionAnalyticsCard key={q.question_id} q={q} totalCompletions={stats.completions} />)}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function QuestionAnalyticsCard({ q, totalCompletions }) {
  const typeLabel = ({
    short_text: "Short text",
    long_text: "Long text",
    single_choice: "Single choice",
    multi_choice: "Multiple choice",
    contact_dynamic: "Contact",
    contact_conditional: "Contact (conditional)",
  })[q.type] || q.type;
  const respondPct = totalCompletions ? Math.round((q.total_responses / totalCompletions) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-xl p-5" data-testid={`lf-q-${q.question_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">{typeLabel}</div>
          <div className="font-medium text-gray-900 mt-1">{q.label}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Responses</div>
          <div className="font-display text-xl font-semibold text-gray-900">
            {q.total_responses}<span className="text-gray-400 text-sm font-normal"> / {totalCompletions || 0}</span>
          </div>
          <div className="text-[11px] text-gray-500">{respondPct}%</div>
        </div>
      </div>

      {/* Choice breakdown */}
      {(q.type === "single_choice" || q.type === "multi_choice" || q.type === "contact_dynamic" || q.type === "contact_conditional") && (
        q.options?.length ? (
          <div className="mt-4">
            <BarList rows={q.options} labelKey="value" />
          </div>
        ) : (
          <div className="text-xs text-gray-400 mt-3">No option data yet.</div>
        )
      )}

      {/* Text sample answers */}
      {(q.type === "short_text" || q.type === "long_text") && (
        q.sample_answers?.length ? (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">Recent answers</div>
            <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {q.sample_answers.map((s, i) => (
                <li key={i} className="text-sm text-gray-800 border-l-2 border-purple-200 pl-3 py-1">
                  "{s.answer}"
                  {s.ts && <span className="ml-2 text-[11px] text-gray-400 font-mono">{s.ts.slice(0, 16).replace("T", " ")}</span>}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-xs text-gray-400 mt-3">No written answers yet.</div>
        )
      )}
    </div>
  );
}

function BarList({ title, rows, labelKey, testId }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <div className={title ? "border border-gray-200 rounded-xl p-5" : ""} data-testid={testId}>
      {title && <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">{title}</div>}
      <div className={title ? "mt-4 space-y-2" : "space-y-2"}>
        {(rows || []).length === 0 && <div className="text-sm text-gray-400">No data yet.</div>}
        {(rows || []).map((r) => (
          <div key={r[labelKey]}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-800">{r[labelKey]}</span>
              <span className="text-gray-500 font-mono">{r.count}</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full brand-gradient rounded-full transition-all" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FIELD_OPTIONS = [["phone", "Phone"], ["email", "Email"], ["company", "Company"], ["job_title", "Job Title"]];

function formatConditional(v) {
  const extras = FIELD_OPTIONS.filter(([k]) => v[k]).map(([k, l]) => `${l}: ${v[k]}`);
  return [`Follow up: ${v.follow_up || "—"}`, ...extras].join(" · ");
}

function ConditionalFieldsPicker({ q, onChange, idx }) {
  const selected = q.fields?.length ? q.fields : ["phone", "email"];
  const toggle = (k) => {
    const next = selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k];
    if (next.length) onChange(FIELD_OPTIONS.map(([key]) => key).filter((key) => next.includes(key)));
  };
  return (
    <div className="mt-3" data-testid={`q-conditional-fields-${idx}`}>
      <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Fields shown when visitor selects "Yes"</label>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {FIELD_OPTIONS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => toggle(k)}
            className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${selected.includes(k) ? "border-purple-600 bg-purple-50 text-purple-900 font-medium" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
            data-testid={`q-field-${idx}-${k}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">Phone and Email are required when shown. Selecting "No" skips to the next question.</p>
    </div>
  );
}
